import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import { T, useT } from '@transifex/react'
import React, { ReactNode } from 'react'
import { DialogActionsStyled, DialogCloseButton, DialogContentStyled } from './Dialog.styled'

type DialogProps = React.ComponentProps<typeof Dialog>

type CustomDialogProps = {
  dialogKey: string
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullScreen?: boolean
  minHeight?: string | number
  maxHeight?: string | number
  onSubmit?: () => Promise<void>
  cancelText?: ReactNode
  submitText?: ReactNode
  title?: ReactNode
  children?: ReactNode
  description?: ReactNode
  onClose?: () => void | boolean
  showCancelOnly?: boolean
  backgroundColor?: string
  showTitle?: boolean
  dialogStyles?: SxProps
  TransitionComponent?: NonNullable<DialogProps['slots']>['transition']
  /** Slide/etc. extras (e.g. `direction`) beyond the generic Dialog transition slot props. */
  TransitionProps?: Record<string, unknown>
  disableAutoFocus?: boolean
  disableEnforceFocus?: boolean
}

export const CustomDialog: React.FC<CustomDialogProps> = ({
  dialogKey,
  maxWidth,
  fullScreen = false,
  minHeight,
  maxHeight,
  onSubmit,
  cancelText,
  submitText,
  title,
  children,
  description,
  onClose,
  showCancelOnly,
  backgroundColor = colors.whiteDark,
  showTitle = true,
  dialogStyles,
  TransitionComponent,
  TransitionProps,
  disableAutoFocus = false,
  disableEnforceFocus = false,
}) => {
  const t = useT()
  const { closeDialog, isOpen } = useDialog(dialogKey)

  const handleOnClose = () => {
    const preventClose = onClose?.() === false
    if (!preventClose) {
      closeDialog()
    }
  }

  const Title = ({ title }: { title: ReactNode }) => (
    <section>
      <Typography variant="subtitle1" sx={{ color: colors.grayDark, mt: 0 }}>
        {title}
      </Typography>
    </section>
  )

  const dialogStyle: SxProps = {
    '.MuiDialog-paper': {
      ...(minHeight ? { minHeight } : {}),
      ...(maxHeight ? { maxHeight } : {}),
    },
  }

  return (
    <Dialog
      aria-label={t('Custom dialog')}
      open={isOpen}
      scroll="paper"
      fullWidth
      disableAutoFocus={disableAutoFocus}
      disableEnforceFocus={disableEnforceFocus}
      fullScreen={fullScreen}
      sx={{ ...dialogStyle, ...dialogStyles }}
      maxWidth={maxWidth}
      id={`${dialogKey}-dialog`}
      onClose={handleOnClose}
      slots={TransitionComponent ? { transition: TransitionComponent } : undefined}
      slotProps={TransitionProps ? { transition: TransitionProps } : undefined}
      data-testid="dialog-test-id">
      {showTitle && (
        <DialogTitle
          className="CustomDialog-title"
          sx={{ borderBottom: `1px solid ${colors.grayExtraLight}` }}>
          {title ? <Title title={title} /> : <Box sx={{ height: 16 }} />}
          <DialogCloseButton id={`${dialogKey}-close`} onClose={handleOnClose} />
        </DialogTitle>
      )}
      {!!description && <DialogContentStyled>{description}</DialogContentStyled>}
      {children ? (
        <>
          {!showTitle && (
            <Box sx={{ position: 'relative' }}>
              <DialogCloseButton id={`${dialogKey}-close`} onClose={handleOnClose} />
              <DialogContentStyled sx={{ backgroundColor }}>{children}</DialogContentStyled>
            </Box>
          )}
          {showTitle && (
            <DialogContentStyled sx={{ backgroundColor }}>{children}</DialogContentStyled>
          )}
        </>
      ) : (
        <DialogActionsStyled>
          <Button
            onClick={handleOnClose}
            variant="outlined"
            data-testid="dialog-cancel-btn"
            id="btnOnCancel">
            {cancelText ? cancelText : <T _str="Cancel" />}
          </Button>
          {!showCancelOnly && (
            <Button
              onClick={onSubmit}
              variant="contained"
              data-testid="dialog-send-btn"
              id="btnOnSubmit">
              {submitText ? submitText : <T _str="Send" />}
            </Button>
          )}
        </DialogActionsStyled>
      )}
    </Dialog>
  )
}
